// $FlowFixMe
import React, { useState, useContext } from 'react';
import { injectIntl } from 'react-intl';
import { pathOr, ascend, descend, prop, sortWith, isNil } from 'ramda';
import moment from 'moment';
import { connect } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Banner } from '@fortress-technology-solutions/fortress-component-library/Atoms';

import {
  checkLeaseEditablility,
  ELECTRONIC_SIGNING_METHOD,
  getIsDisabledMonthly,
  getLeasesOptions,
  getSelectedLease,
  parseAffordableValues,
  getMonthlyRecurringCharges,
  parseRentValues,
} from '../../utils/lease-helpers.js';
import { getPreLeaseMoveInDate } from '../ManageProspects/utils';
import {
  getCurrentNonOptionalCharge,
  getIsSection236,
} from '../../utils/affordable';

import type { GlobalState } from '../App/types';

import ApplicantHeader from './ApplicantHeader';
import ResidentHeader from './ResidentHeader';
import LeaseDataTabForm from './LeaseDataTabForm';
import {
  useAsyncFloorPlanPricing,
  useAsyncHasOpenAffordableQualification,
  useAsyncMonthlyLeaseRequiredFees,
  useRenewalBlockedByActiveTransfer,
} from './hooks';
import type { LeaseDataTabProps } from './types';
import { formatDateLocale } from '../../utils/date-helpers';
import { getApplication } from '../ApplicationProfile/selectors';
import { useFetchSavedRentSteps } from '../../hooks/data-fetching/useFetchLeaseRentSteps.js';
import { LeaseDataTabContext } from './context.js';
import messages from './messages';
import { hasTrackedAutoFilledLeaseChargeField } from '../../constants/autoFilledLeaseChargeFields';

export const valueNameSort = sortWith([
  descend(prop('value')),
  ascend(prop('label')),
]);

const LeaseDataTab = ({
  allAffordableTabsApproved,
  unapprovedAffordablePrograms = [],
  moveInCertsApproved = true,
  unapprovedMoveInPrograms = [],
  applicationId,
  basicLeaseFees,
  doNotRenew,
  frNames,
  history,
  handleRenewal,
  handleTransfer,
  handleTransferComplete,
  hasNonFR,
  intl,
  isDisabledRenewal,
  isDisabledTransfer,
  isResident,
  labelEndDate,
  lateMethods,
  leases,
  leaseRentPercentage,
  leaseSignatureStatuses,
  leaseTerms,
  onGenerate,
  onSubmit,
  removeLease,
  residentId,
  openFiscalPeriod,
  prospectInfo,
  securityDeposits,
  selectedMonthlyOption,
  selectedProperty,
  submittingRenewal,
  transferInformation,
  utilityAllowanceId,
  softDeletedLeaseTerms,
  startTransferMessage,
  startRenewalMessage,
  underEviction,
  noticeToVacate,
  currentApplication,
  nonOptionalCharge: nonOptionalChargeFromQualification,
  application,
  householdId,
  hasOpenTransfer,
  setProratedRentAmount,
  setProratedOtherAmount,
  resetProratedRentAutoFilledChangeStatus,
  handleAutoFilledFieldChange,
  autoFilledLeaseChargeFields,
}: LeaseDataTabProps) => {
  const flags = useFlags();
  const {
    assigningUnitsMoveInDates: assigningUnitsMoveInDatesFlag,
    section236: section236Flag,
    unitLevelPrice,
    automatedLeaseChargesP1,
    // aliased: `quotingRent` is also the parsed rent value further down
    quotingRent: quotingRentFlag,
    affordableWaitlistV2: affordableWaitlistV2Flag,
  } = flags;
  const newestLease = leases.length > 0 ? leases[0] : {};
  // ENG-30334 — the end date of the lease an in-progress renewal is renewing. `leases` is ordered
  // newest-first and a renewal is created as the newest lease, so the lease it replaces is the
  // next one down — the same index `selectPreviousLease` below already treats as "the previous
  // lease", reused rather than introducing a second notion of it. useRecommendedLeaseRent needs
  // this to derive a renewal's pricing date while the renewal's own Lease Start Date is empty;
  // without it that hook fell back to the household's original move-in and priced the wrong
  // period. Null for a first lease, which the hook treats as "no end date" and prices as of today.
  const currentLeaseEndDate = leases.length > 1 ? leases[1]?.endDate : null;
  const [formDirty, setFormDirty] = useState(false);

  const { setSelectedLeaseId, selectedLeaseId } =
    useContext(LeaseDataTabContext);

  const moveInDate = pathOr(null, ['moveInDate'], newestLease);
  const startDate = pathOr(null, ['startDate'], newestLease);
  const initialLeaseDates = {
    moveInDate: moveInDate ? moment(moveInDate) : '',
    startDate: startDate ? moment(startDate) : '',
  };
  const [leaseDates, setLeaseDates] = useState(initialLeaseDates);

  const selectPreviousLease = () => {
    const previousLease = leases.length > 1 ? leases[1] : null;
    if (!previousLease) {
      return null;
    }
    setSelectedLeaseId(previousLease.id);
  };

  const handleLeaseChange = ({ target: { value } }) => {
    setSelectedLeaseId(value);
  };

  const updateLeaseToNewRenewalLease = (leases: Array<Object>) => {
    const renewalLease = leases[0];
    setSelectedLeaseId(renewalLease.id);
  };

  const updateLeaseDate = (key: string, value: Object) =>
    setLeaseDates({ ...leaseDates, [key]: value });

  const lease = getSelectedLease(leases, selectedLeaseId, flags);
  const unit = lease?.units?.[0] || {};
  const floorplanId = unit?.floorPlan?.id ?? null;
  const isAffordable = unit?.floorPlan?.isAffordable ?? false;
  /**
   * Hook only fires the call to the get floorplanPricings if there's a
   * floorplan and if it's affordable. Returns empty object otherwise.
   */
  const affordableFloorplanPricing = useAsyncFloorPlanPricing(
    floorplanId,
    isAffordable,
    selectedProperty,
  );
  const applicableRents = selectedProperty.setup?.rdProjectType?.rdrents || {};

  const renewalBlockedByTransfer = useRenewalBlockedByActiveTransfer({
    isResident,
    householdId: householdId ?? application?.householdId,
    selectedProperty,
    loadingFallback: hasOpenTransfer,
  });

  const {
    leaseMonthlyRequiredFees,
    updateRequiredFeesList,
    requiredFeesUpdated,
  } = useAsyncMonthlyLeaseRequiredFees(lease.id, lease.lockedFees);

  const { hasOpenAffordableQualifications, hasOpenGrossRentCertification } =
    useAsyncHasOpenAffordableQualification(
      selectedProperty.organizationId,
      selectedProperty.id,
      residentId,
      application?.propertyClass?.name === 'Affordable',
    );

  const affordablePricing = parseAffordableValues(
    affordableFloorplanPricing,
    leaseDates,
    lease.isRenewal,
    unit,
    utilityAllowanceId,
  );
  const nonOptionalCharge =
    nonOptionalChargeFromQualification ??
    getCurrentNonOptionalCharge(
      affordableFloorplanPricing?.nonOptionalCharges ?? [],
    );
  const affordableValues = {
    isAffordable,
    utilityAllowanceId,
    isReceivingAssistance: lease.receivesHousingAssistance,
    nonOptionalCharge,
    ...affordablePricing,
  };

  const isEditable = checkLeaseEditablility(lease, residentId);

  const leasesOptions = getLeasesOptions(leases, isResident);
  const isDisabledMonthly = getIsDisabledMonthly(
    lease,
    leasesOptions,
    residentId,
  );
  /**
   * TODO: I'd like to remove this when we move the `Monthly Lease Charges or
   * Credits` out of LDT. This parsing of `initialValues` is being done because
   * that section saves the lease, and the updated lease has null for the
   * `desiredSignatureMethod` until the page refreshes.
   */
  let desiredSignatureMethod = pathOr(
    'default',
    ['desiredSignatureMethod'],
    lease,
  );
  if (desiredSignatureMethod === ELECTRONIC_SIGNING_METHOD && hasNonFR) {
    desiredSignatureMethod = 'default';
  }

  const floorPlan = unit?.floorPlan;
  const isSection236 =
    section236Flag && getIsSection236(selectedProperty, floorPlan);

  const {
    marketRent,
    quotingRent,
    basicRent,
    noteRent,
    hud236BasicRent,
    hud236MarketRent,
  } = parseRentValues(affordableValues, unit, lease, isSection236, {
    unitLevelPrice,
    quotingRent: quotingRentFlag,
  });

  const initialValuesStandard = {
    ...lease,
    marketRentValue: marketRent,
    quotingRentValue: quotingRent,
    basicRent,
    noteRent,
    hud236BasicRent,
    hud236MarketRent,
    isReceivingAssistance: lease.receivesHousingAssistance,
    desiredSignatureMethod,
    overrideLeaseExpirationLimit: !isNil(
      lease.overrideLeaseExpirationLimitUpdatedAt,
    ),
  };

  const preLeaseMoveInDate = getPreLeaseMoveInDate({
    prospectPreferences: prospectInfo?.prospectPreferences,
    isWaitlistV2Enabled: affordableWaitlistV2Flag,
  });

  const initialValuesFlag = {
    ...lease,
    marketRentValue: marketRent,
    quotingRentValue: quotingRent,
    basicRent,
    noteRent,
    hud236BasicRent,
    hud236MarketRent,
    isReceivingAssistance: lease.receivesHousingAssistance,
    desiredSignatureMethod,
    overrideLeaseExpirationLimit: !isNil(
      lease.overrideLeaseExpirationLimitUpdatedAt,
    ),
    moveInDate:
      lease?.moveInDate ||
      currentApplication?.au?.lease?.moveInDate ||
      (preLeaseMoveInDate
        ? formatDateLocale(preLeaseMoveInDate, 'MM/DD/YYYY')
        : null),
  };

  const initialValues = assigningUnitsMoveInDatesFlag
    ? initialValuesFlag
    : initialValuesStandard;

  const configuredUnitFees = getMonthlyRecurringCharges(unit);
  let monthlyRecurringCharges = lease.lockedFees
    ? leaseMonthlyRequiredFees
    : configuredUnitFees;

  // Sort by amount//name
  monthlyRecurringCharges = valueNameSort(monthlyRecurringCharges);
  const activeMonthlyRecurringFees = monthlyRecurringCharges.filter(
    (fee) => !fee.isRemoved,
  );

  const { data: priorLeaseRentSteps } = useFetchSavedRentSteps({
    leaseId: lease.priorLeaseId,
    lease,
  });

  const atLeastOneLeaseChargeFieldAutoFilled =
    hasTrackedAutoFilledLeaseChargeField(autoFilledLeaseChargeFields);

  const showAutoFilledLeaseChargeFieldsWarning =
    automatedLeaseChargesP1 &&
    atLeastOneLeaseChargeFieldAutoFilled &&
    !lease.isLocked;

  return (
    <div className="container-fluid">
      {!residentId && <ApplicantHeader frNames={frNames} />}
      <Banner
        status="warning"
        message={intl.formatMessage(
          messages.prePopulatedLeaseChargeFieldsWarning,
        )}
        open={showAutoFilledLeaseChargeFieldsWarning}
        sx={{
          width: '50%',
          mb: 2,
        }}
      />
      {residentId && (
        <ResidentHeader
          applicationId={applicationId}
          application={application}
          doNotRenew={doNotRenew}
          formDirty={formDirty}
          history={history}
          handleLeaseChange={handleLeaseChange}
          handleRenewal={handleRenewal}
          handleTransfer={handleTransfer}
          handleTransferComplete={handleTransferComplete}
          hasOpenAffordableQualifications={hasOpenAffordableQualifications}
          hasOpenGrossRentCertification={hasOpenGrossRentCertification}
          isDisabledRenewal={isDisabledRenewal}
          renewalBlockedByTransfer={renewalBlockedByTransfer}
          isDisabledTransfer={isDisabledTransfer}
          isResident={isResident}
          lease={lease}
          leasesOptions={leasesOptions}
          leaseTerms={leaseTerms}
          removeLease={removeLease}
          residentId={residentId}
          selectPreviousLease={selectPreviousLease}
          submittingRenewal={submittingRenewal}
          updateLeaseToNewRenewalLease={updateLeaseToNewRenewalLease}
          startTransferMessage={startTransferMessage}
          startRenewalMessage={startRenewalMessage}
          underEviction={underEviction}
          noticeToVacate={noticeToVacate}
        />
      )}
      <LeaseDataTabForm
        affordableValues={affordableValues}
        applicableRents={applicableRents}
        applicationId={applicationId}
        basicLeaseFees={basicLeaseFees}
        currentLeaseEndDate={currentLeaseEndDate}
        frNames={frNames}
        hasNonFR={hasNonFR}
        initialValues={{
          ...initialValues,
          utilityAllowanceAmount: affordablePricing?.utilityAllowanceAmount,
          monthlyRecurringCharges: activeMonthlyRecurringFees,
        }}
        allAffordableTabsApproved={allAffordableTabsApproved}
        unapprovedAffordablePrograms={unapprovedAffordablePrograms}
        moveInCertsApproved={moveInCertsApproved}
        unapprovedMoveInPrograms={unapprovedMoveInPrograms}
        householdId={application?.householdId}
        intl={intl}
        isDisabledMonthly={isDisabledMonthly}
        isEditable={isEditable}
        labelEndDate={labelEndDate}
        lateMethods={lateMethods}
        lease={lease}
        priorLease={leases[1] || {}}
        leaseRentPercentage={leaseRentPercentage}
        leaseSignatureStatuses={leaseSignatureStatuses}
        leaseTerms={leaseTerms}
        softDeletedLeaseTerms={softDeletedLeaseTerms}
        onGenerate={onGenerate}
        onSubmit={onSubmit}
        openFiscalPeriod={openFiscalPeriod}
        prospectInfo={prospectInfo}
        residentId={residentId}
        securityDeposits={securityDeposits}
        selectedMonthlyOption={selectedMonthlyOption}
        setFormDirty={setFormDirty}
        transferInformation={transferInformation}
        unit={unit}
        updateLeaseDate={updateLeaseDate}
        monthlyRecurringCharges={monthlyRecurringCharges}
        updateRequiredFeesList={updateRequiredFeesList}
        requiredFeesUpdated={requiredFeesUpdated}
        noticeToVacate={noticeToVacate}
        utilityAllowanceId={utilityAllowanceId}
        affordableFloorplanPricing={affordableFloorplanPricing}
        priorLeaseRentSteps={priorLeaseRentSteps}
        setProratedRentAmount={setProratedRentAmount}
        setProratedOtherAmount={setProratedOtherAmount}
        resetProratedRentAutoFilledChangeStatus={
          resetProratedRentAutoFilledChangeStatus
        }
        handleAutoFilledFieldChange={handleAutoFilledFieldChange}
        autoFilledLeaseChargeFields={autoFilledLeaseChargeFields}
      />
    </div>
  );
};

export const mapStateToProps = (state: GlobalState): Object => {
  const {
    app: { selectedProperty },
  } = state;
  return {
    selectedProperty,
    currentApplication: getApplication(state),
  };
};

export default connect(mapStateToProps)(injectIntl(LeaseDataTab));
